import { Check, Edit2, MessageCircle, Trash2, X } from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { ProjectListItem } from "@/features/project";

const formatDate = (timestamp: number) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

interface ProjectItemProps {
  project: ProjectListItem;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRename: (newTitle: string) => void;
}

export const ProjectItem: React.FC<ProjectItemProps> = ({
  project,
  isActive,
  onSelect,
  onDelete,
  onRename,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditTitle(project.title);
  };

  const handleSaveEdit = () => {
    if (editTitle.trim()) {
      onRename(editTitle.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditTitle("");
  };

  return (
    <div
      onClick={() => !isEditing && onSelect()}
      className={`group p-3 cursor-pointer border-b border-slate-800 transition-colors ${
        isActive ? "bg-slate-800" : "hover:bg-slate-800/50"
      }`}
    >
      {isEditing ? (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveEdit();
              if (e.key === "Escape") handleCancelEdit();
            }}
            className="flex-1 bg-slate-900 md:text-xs text-slate-200 px-2 py-1 rounded border border-slate-700 focus:outline-none focus:border-indigo-500"
            autoFocus
          />
          <button onClick={handleSaveEdit} className="p-1 text-green-500 hover:text-green-400">
            <Check size={14} />
          </button>
          <button onClick={handleCancelEdit} className="p-1 text-slate-500 hover:text-slate-400">
            <X size={14} />
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-xs font-medium text-slate-200 line-clamp-2 flex-1">
              {project.title}
            </h3>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartEdit();
                }}
                className="p-1 text-slate-500 hover:text-slate-300"
                title="Rename"
              >
                <Edit2 size={12} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm("Delete this project?")) {
                    onDelete();
                  }
                }}
                className="p-1 text-slate-500 hover:text-red-400"
                title="Delete"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span>{formatDate(project.updatedAt)}</span>
            {project.messageCount > 0 && (
              <span className="flex items-center gap-1 text-slate-400">
                <MessageCircle size={10} />
                {project.messageCount}
              </span>
            )}
            {project.totalCost != null && (
              <span className="text-emerald-500">${project.totalCost.toFixed(4)}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
};
